(function (root) {
  'use strict';
  const error = (code) => Object.assign(new Error(code), { code });

  function quantities(variant, count = 4) {
    if (!variant?.available) return [];
    const step = Math.max(1, Number(variant.increment) || 1);
    const min = Math.max(step, Math.ceil((Number(variant.min) || 1) / step) * step);
    const max = variant.max == null ? Infinity : Number(variant.max);
    return Array.from({ length: count }, (_, index) => min + index * step).filter((quantity) => quantity <= max);
  }

  function buildItems(variants, selections) {
    if (!Array.isArray(selections) || !selections.length) throw error('empty');
    const items = new Map();
    for (const selection of selections) {
      const variant = variants.find((entry) => String(entry.id) === String(selection.id));
      if (!variant?.available) throw error('sold_out');
      const quantity = Number(selection.quantity);
      if (!Number.isSafeInteger(quantity) || quantity < 1) throw error('quantity_error');
      const key = String(variant.id);
      items.set(key, { id: variant.id, quantity: (items.get(key)?.quantity || 0) + quantity });
    }
    for (const item of items.values()) {
      const variant = variants.find((entry) => String(entry.id) === String(item.id));
      if (item.quantity < (variant.min || 1) || item.quantity % (variant.increment || 1) !== 0 ||
          (variant.max != null && item.quantity > variant.max)) throw error('quantity_error');
    }
    return [...items.values()];
  }

  function discountCodes(cart) {
    const codes = new Map();
    const add = (title) => title && codes.set(String(title).toLowerCase(), String(title));
    for (const entry of cart.discount_codes || []) if (entry.applicable === true) add(entry.code);
    const applications = [...(cart.cart_level_discount_applications || [])];
    for (const item of cart.items || []) {
      for (const entry of item.line_level_discount_allocations || []) applications.push(entry.discount_application);
    }
    for (const application of applications) if (application?.type === 'discount_code') add(application.title);
    return [...codes.values()];
  }

  function requestedDiscountCodes(cart) {
    const codes = new Map();
    for (const code of discountCodes(cart)) codes.set(code.toLowerCase(), code);
    for (const entry of cart.discount_codes || []) {
      if (entry.code) codes.set(String(entry.code).toLowerCase(), String(entry.code));
    }
    return [...codes.values()];
  }

  function createQueue(onChange = () => {}) {
    let tail = Promise.resolve();
    let pending = 0;
    let revision = 0;
    let lastError = null;
    const settled = async () => {
      do { const current = tail; await current; if (current === tail) break; } while (true);
    };
    return {
      run(operation) {
        if (pending === 0) lastError = null;
        pending++;
        revision++;
        onChange(pending);
        const result = tail.then(async () => {
          const value = await operation();
          lastError = null;
          return value;
        }).catch((failure) => {
          lastError = lastError || failure;
          throw failure;
        }).finally(() => {
          pending--;
          onChange(pending);
        });
        tail = result.catch(() => {});
        return result;
      },
      settled,
      async idle() {
        await settled();
        if (lastError) throw lastError;
      },
      isCurrent(expectedRevision) {
        if (lastError) throw lastError;
        return pending === 0 && revision === expectedRevision;
      },
      recordFailure(failure) { lastError = lastError || failure; },
      get pending() { return pending; },
      get revision() { return revision; },
      acknowledge(expectedRevision) {
        if (pending !== 0 || revision !== expectedRevision) return false;
        lastError = null;
        return true;
      }
    };
  }

  async function readJSON(response) {
    let data;
    try { data = await response.json(); } catch { throw error('unconfirmed'); }
    if (!response.ok || data.status >= 400 || data.errors) {
      const message = typeof data.description === 'string' ? data.description :
        typeof data.errors === 'string' ? data.errors : data.message || 'request_failed';
      throw Object.assign(new Error(message), { code: 'request_failed', response: data, status: response.status });
    }
    return data;
  }

  async function replaceVariant(request, cart, lineKey, variantId) {
    const line = cart.items.find((item) => item.key === lineKey);
    if (!line) throw error('changed');
    if (String(line.variant_id) === String(variantId)) return cart;
    if (line.selling_plan_allocation || line.parent_relationship || line.item_components?.length ||
        line.instructions?.can_remove === false || line.instructions?.can_update_quantity === false) throw error('changed');
    await request('add', { items: [{ id: variantId, quantity: line.quantity, properties: line.properties || {} }] });
    try {
      const afterAdd = await request('get');
      const candidates = afterAdd.items.filter((item) => String(item.variant_id) === String(line.variant_id) &&
        JSON.stringify(item.properties || {}) === JSON.stringify(line.properties || {}) && !item.selling_plan_allocation);
      const original = afterAdd.items.find((item) => item.key === lineKey) || (candidates.length === 1 ? candidates[0] : null);
      if (!original) throw error('changed');
      return await request('change', { id: original.key, quantity: 0 });
    } catch (failure) {
      throw Object.assign(error('swap_partial'), { cause: failure });
    }
  }

  // Volume tiers must mirror the automatic cart discounts: qty2=10%, qty3=15%, qty4+=20%.
  function tierDiscount(quantity) {
    const q = Number(quantity) || 1;
    if (q >= 4) return 20;
    if (q === 3) return 15;
    if (q === 2) return 10;
    return 0;
  }

  const core = { quantities, tierDiscount, buildItems, discountCodes, requestedDiscountCodes, createQueue, readJSON, replaceVariant };
  if (typeof module === 'object' && module.exports) module.exports = core;
  else root.FleetCartCore = core;
})(typeof globalThis === 'object' ? globalThis : this);
